import {Injectable} from '@angular/core';
import {BehaviorSubject, catchError, firstValueFrom, Observable, of, switchMap, tap} from 'rxjs';
import {HttpClient, HttpHeaders, HttpParams} from '@angular/common/http';
import {HelperService} from '../../services/helper.service';
import {UserAuthenticationService} from '../../services/user-authentication.service';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {LocalStorageService} from '../../local-storage.service';
import {ServiceAppModels} from '../../models/serviceapp-models';
import {DokumentModels} from '../../models/dokument-models';
import {DokumentDataService} from '../../services/dokument-data.service';
import {Router} from '@angular/router';
import {BenutzerModels} from '../../models/benutzer-models';
import {map, startWith} from 'rxjs/operators';
import DokumentInfo = DokumentModels.DokumentInfo;
import DokumentTyp = DokumentModels.DokumentTyp;
import DokumentSource = DokumentModels.DokumentSource;


export interface Document {
    documentID: string;
    documentName: string;
    description: string;
    modifiedBy: string;
    modified: string;
    SA_TransDocType: string;
    objectID: string;
    icon: string;
}


@Injectable({
    providedIn: 'root',
})
export class TimeAppDocumentsService {
    private readonly documents$: BehaviorSubject<Document[]> = new BehaviorSubject([] as Document[]);
    private readonly objectID$: BehaviorSubject<string> = new BehaviorSubject('');

    constructor(private http: HttpClient, private helperService: HelperService,
                private userAuthService: UserAuthenticationService,
                private localStorageService: LocalStorageService,
                private documentService: DokumentDataService,
                private router: Router) {

        this.objectID$.pipe(
            takeUntilDestroyed(),
            switchMap(id => this.getObjectDocuments(id)),
            map(a => {
                return a.map(d => ({...d, icon: this.getDocumentIconByFileExtension(d)}));
            }),
            tap(documents => this.documents$.next(documents)),
            catchError((err, caught) => of(err).pipe(
                tap(err => {
                    this.documents$.next([]);
                    console.error(err);
                }),
                startWith([])
            ))
        ).subscribe();
    }

    public getDocument(documentID: string): Document | undefined {
        return this.documents$.getValue().find(document => document.documentID == documentID);
    }

    public getDocuments(): Observable<Document[]> {
        return this.documents$.asObservable();
    }

    public loadDocuments(objectID: string) {
        this.objectID$.next(objectID);
    }

    public async downloadDocument(documentID: string) {
        const document = this.documents$.getValue().find(doc => doc.documentID == documentID)!;

        this.getFile(document.documentID).subscribe({
            next: (response) => {
                let dataType = response.type;
                let binaryData: any[] = [];
                binaryData.push(response);
                let downloadLink = window.document.createElement('a');
                downloadLink.href = window.URL.createObjectURL(new Blob(binaryData, {type: dataType}));
                downloadLink.setAttribute('download', document.documentName);
                window.document.body.appendChild(downloadLink);
                downloadLink.click();
                window.document.body.removeChild(downloadLink);
            },
        });
    }

    public getFile(documentID: string) {
        const headers = new HttpHeaders({
            Authorization: this.helperService.getSwissAuthorization(this.userAuthService.currentUserInfo),
        });
        const url = this.helperService.getSwissWebServiceConfig()!.dokuUrl;
        return this.http.get<any>(url + '/DownloadDatei?id=' + documentID + '&loginname=' + this.userAuthService.getCurrentAuthenticatedUser()?.loginname, {headers: headers, responseType: 'blob' as 'json'});
    }

    public async showDocument(documentID: any) {
        const document = this.documents$.getValue().find(doc => doc.documentID == documentID)!;
        switch (document.icon) {
            case 'pdf.png':
                if (await this.userAuthService.hasLicences(BenutzerModels.UserLicenses.CNavigatorMobile)) {
                    const pdfList = this.documents$.getValue().filter(doc => doc.icon === 'pdf.png').flatMap<DokumentInfo>(pdfActivity => {
                        return {
                            objectID: pdfActivity.objectID,
                            documentID: pdfActivity.documentID,
                            description: pdfActivity.description,
                            fileName: pdfActivity.documentName,
                            dokumentTyp: DokumentTyp.Notizblatt,
                            dokumentQuelle: DokumentSource.FromWebService,
                            lastModified: new Date()
                        };
                    });

                    this.documentService.setPDFDokument(document.objectID, document.documentID, document.documentName, document.description, DokumentModels.DokumentTyp.Dokument, DokumentModels.DokumentSource.FromWebService, new Date(document.modified), pdfList);
                    this.router.navigate(['pdfviewer', document.documentID], {queryParams: {returnUrl: 'timeapp/documents/' + document.objectID}});
                } else {
                    await this.downloadDocument(documentID);
                }
                break;
            case 'photo.png':
                this.getFile(documentID).subscribe({
                    next: (file: { type: any }) => {
                        const binaryData: BlobPart[] = [];
                        binaryData.push(file as BlobPart);
                        window.location.href = window.URL.createObjectURL(new File(binaryData, document.documentName, {type: 'image/jpeg'}));
                    },
                    error: () => {
                        throw new Error('file not found');
                    }
                });
                break;
            default:
                console.log(document.documentID);
        }
    }

    //todo duplicated from document-data.service
    public uploadDokumentWithInfo(dokuInfo: DokumentModels.DokumentInfo, file: File) {
        const formData = new FormData();
        formData.append('fileName', dokuInfo.fileName);
        formData.append('description', dokuInfo.description);
        formData.append('documentType', dokuInfo.dokumentTyp);
        formData.append('objectID', dokuInfo.objectID);
        formData.append('documentID', dokuInfo.documentID);
        formData.append('concurrencyCheck', (dokuInfo.lastModified || new Date()).toISOString());  //TODO get timestamp from somewhere, make sure to include timezone
        formData.append('file', file);

        const url = this.helperService.getSwissWebServiceConfig()!.dokuUrl;

        return this.http.post<Response>(url + '/UploadDoku', formData);
    }

    //TODO maybe use this instead of getObjectActivities and make sure logic can be reused
    async getObjektDokumenteFromTo(withFileModifiedInfo = true): Promise<ServiceAppModels.SA_Document[]> {
        let syncConfig: ServiceAppModels.SynchronizationPeriod = await this.localStorageService.get('service-app-mobile-config', 'SwissWebConfigDB', false, ServiceAppModels.DEFAULT_SYNC_PERIOD);

        let url = this.helperService.getSwissWebServiceConfig()!.saUrl + '/ObjektDokumenteFromTo?mandant=' + this.userAuthService.getCurrentAuthenticatedUser()?.mandatorID + '&withFileModifiedInfo=' + withFileModifiedInfo + '&DaysInPast=' + (syncConfig.syncInPast || ServiceAppModels.DEFAULT_SYNC_PERIOD.syncInPast) + '&DaysInFuture=' + (syncConfig.syncInFuture || ServiceAppModels.DEFAULT_SYNC_PERIOD.syncInFuture) + '&SendUnfinishedOrders=' + (syncConfig.notClosedOrder || ServiceAppModels.DEFAULT_SYNC_PERIOD.notClosedOrder);

        return firstValueFrom(this.http.get<ServiceAppModels.SA_Document[]>(url, this.helperService.getSwissHttpOptions(this.userAuthService.currentUserInfo)));
    }

    public getNewFileName(fileName: string): string {
        const fileExtension = fileName.slice(fileName.lastIndexOf('.'));
        const baseName = fileName.slice(0, fileName.lastIndexOf('.'));
        let newName = fileName;
        let counter = 0;
        const docs = this.documents$.getValue();
        while (!this.isUniqueName(newName, docs)) {
            counter++;
            newName = `${baseName}_${counter}${fileExtension}`;
        }
        return newName;
    }

    public deleteDoku(objectID: string, documentID: string) {
        const formData = new FormData();

        const headers = new HttpHeaders({
            Authorization: this.helperService.getSwissAuthorization(this.userAuthService.currentUserInfo),
        });

        const params: HttpParams = new HttpParams()
            .set('objectID', objectID)
            .set('documentID', documentID);

        const options = {
            headers: headers, params: params,
        };

        const url = this.helperService.getSwissWebServiceConfig()!.saUrl;
        return this.http.post<Response>(url + '/DeleteDoku', formData, options);
    }

    private getObjectDocuments(objectID: string): Observable<Document[]> {
        return this.http.get<Document[]>(this.helperService.getSwissWebServiceConfig()!.tishUrl + `/GetOrderFiles?ObjectID=${objectID}&MandantID=${this.userAuthService.getCurrentMandant().mandatorID}`, this.helperService.getSwissHttpOptions(this.userAuthService.currentUserInfo));
    }

    private isUniqueName(name: string, dataSource: Document[]): boolean {
        return dataSource.findIndex((result) => result.documentName === name) === -1;
    }

    private getDocumentIconByFileExtension(document: Document): string {
        const ext = document.documentName.substring(document.documentName.lastIndexOf('.'));
        switch (ext) {
            case '.png':
            case '.jpg':
            case '.jpeg':
            case '.bmp':
            case '.gif':
            case '.tiff':
                return 'photo.png';
            case '.xls':
            case '.xlsm':
                return 'xls.png';
            case '.s3a':
                return 'g.icon';
            case '.docx':
            case '.doc':
                return 'doc.jpg';
            case '.pdf':
                return 'pdf.png';
            case '.msg':
                return 'email.png';
            case '.xml':
                return 'xml.png';
            case '.zip':
                return 'zip.png';
            case '.txt':
                return 'text.png';
            default:
                return 'unknown.png';
        }
    }
}
